import Vue from 'vue'
import VueRouter from 'vue-router'
import catalogRoutes from './catalogRoutes'
import hedgeRoutes from './hedgeRoutes'
import inventoryRoutes from './inventoryRoutes'
import financeRoutes from './financeRoutes'
import logisticsRoutes from './logisticsRoutes'
import lotRoutes from './lotRoutes'
import purchaseRoutes from './purchaseRoutes'
import yardRoutes from './yardRoutes'
import warehouseRoutes from './warehouseRoutes'
import publicRoutes from '@/router/publicRoutes'
import axios from 'axios'
import config from '@/../izzi_config'

Vue.use(VueRouter)

/**
 * gotoDoc and urlToRoute mixins utilize reverse to get correct urls
 * the format for reverse should be either 'documents-single' or 'documents-many'
 * This enables the resolution of Doc names Document-ID and urls https://api.trackkat.com/api/DOCUMENT/ID/
 */

let routes = [
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import(/* webpackChunkName: "index-routes" */ '@/views/Dashboard/Dashboard')
  },
  {
    path: '/calendar',
    name: 'calendar',
    component: () => import(/* webpackChunkName: "index-routes" */ '@/views/Calendar/CalendarView'),
    meta: { permission: 'frontend.view_calendar' }
  },
  {
    path: '/user-profile',
    name: 'user-profile',
    component: () => import(/* webpackChunkName: "index-routes" */ '@/views/UserProfile/UserProfile')
  },
  {
    path: '/report-list',
    name: 'reports',
    component: () => import(/* webpackChunkName: "index-routes" */ '@/views/Reports/ReportList'),
    meta: { permission: 'frontend.view_reports' }
  },
  ...publicRoutes,
  ...catalogRoutes,
  ...hedgeRoutes,
  ...financeRoutes,
  ...logisticsRoutes,
  ...lotRoutes,
  ...purchaseRoutes,
  ...inventoryRoutes,
  ...yardRoutes,
  ...warehouseRoutes,
  {
    path: '*',
    name: '404',
    component: () => import(/* webpackChunkName: "index-routes" */ '@/views/PageNotFound.vue')
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

/**
 * Updates a string with params like :id with matching variable from params
 * @param template {string}
 * @param params {Object}
 * @returns {*}
 */
function urlTemplate (template, params) {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] !== undefined ? params[key] : match
  })
}

/**
 * Intercepts the to.meta.redirect attribute, prepending config.frontend and replacing with to.params to redirect the
 * user agent to the new frontend url.
 */
router.beforeEach((to, from, next) => {
  if (to.meta?.redirect) {
    const redirectUrl = config.frontend + urlTemplate(to.meta.redirect, to.params)
    // console.log(to.params, to.meta.redirect, redirectUrl)
    window.location = redirectUrl
  } else {
    next()
  }
})

router.beforeEach((to, from, next) => {
  if (to.path === '/' && to.hash.startsWith('#/')) {
    next(to.hash.slice(1))
  } else {
    next()
  }
})

/**
 * Dynamically set the page titles
 */
router.beforeEach((to, from, next) => {
  if (to.name) {
    let nameSet = to.name
      .split('-')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
    if (to.params.id) nameSet.push(to.params.id)
    if (to.query.search) nameSet.push('search for ' + to.query.search)
    if (to.query.page) nameSet.push('#' + to.query.page)
    nameSet.push('- TrackKat.com')
    document.title = nameSet.join(' ')
  }
  next()
})

/**
 * Log user route changes
 */
router.beforeEach((to, from, next) => {
  Vue.prototype.$logger.info(`User navigation URL=${window.location} PAGE=${to.name}`)
  next()
})

/**
 * Send User page information to api
 */
router.afterEach((to, _, failure) => {
  if (failure) return
  try {
    if (Vue.prototype.$auth.user) {
      axios.post('/api/page_tracking/', {
        url: to.fullPath,
        username: Vue.prototype.$auth.user?.email
      }).catch((err) => {
        Vue.prototype.$logger.warn(err.message)
      })
    }
  } catch {
    Vue.prototype.$logger.warn('Error occurred attempting to send page route to api')
  }
})

export default router
